import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  NumberFilterModel,
  TextFilterModel,
  DateFilterModel,
  ICombinedSimpleModel
} from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import type { JSX } from 'react';
import React from 'react';

import type { BillingReportRow } from '../types';
import { findRowFlagFilterOption } from '../utils/rowFlagFilterOptions';
import { titleize } from '../utils/titleize';

const FILTER_LOCALE_TEXT = {
  equals: 'equals',
  notEqual: 'does not equal',
  lessThan: 'less than',
  greaterThan: 'greater than',
  inRange: 'between',
  inRangeStart: 'from',
  inRangeEnd: 'to',
  lessThanOrEqual: 'less than or equal to',
  greaterThanOrEqual: 'greater than or equal to',
  contains: 'contains',
  notContains: 'does not contain',
  startsWith: 'begins with',
  endsWith: 'ends with',
  blank: 'blank',
  notBlank: 'not blank',
  before: 'before',
  after: 'after',
  andCondition: 'and',
  orCondition: 'or'
};

interface FilterExplanationProps {
  field: string;
  filterModel:
    | TextFilterModel
    | NumberFilterModel
    | DateFilterModel
    | ICombinedSimpleModel<TextFilterModel | NumberFilterModel>;
  gridRef?: React.RefObject<AgGridReact<BillingReportRow> | null>;
  onClear?: (field: string) => void;
}

const ClearFilterButton = ({
  field,
  onClear
}: {
  field: string;
  onClear?: (field: string) => void;
}): JSX.Element | null => {
  if (!onClear) return null;
  return (
    <button
      type="button"
      aria-label={`Clear ${titleize(field)} filter`}
      className="ml-1 text-gray-300 hover:text-gray-500 text-xs"
      onClick={() => onClear(field)}>
      <FontAwesomeIcon icon={faXmark} />
    </button>
  );
};

const chipClassName =
  'inline-flex items-center h-[26px] pl-2.5 pr-1.5 rounded-md bg-white border border-gray-200 text-[12.5px] text-gray-900 whitespace-nowrap';
const operatorClassName = 'text-gray-400 px-1';
const valueClassName = 'font-mono font-medium text-gray-900';

function isCombinedFilter(
  filterModel:
    | TextFilterModel
    | NumberFilterModel
    | DateFilterModel
    | ICombinedSimpleModel<TextFilterModel | NumberFilterModel>
): filterModel is ICombinedSimpleModel<TextFilterModel | NumberFilterModel> {
  return (
    (filterModel as ICombinedSimpleModel<TextFilterModel | NumberFilterModel>).conditions !==
    undefined
  );
}

// The Status and flag columns' fields don't match their headers.
const FIELD_LABELS: Record<string, string> = { errors: 'Status', indicators: 'Flags' };
const fieldLabel = (field: string): string => FIELD_LABELS[field] ?? titleize(field);

const FilterExplanation = ({
  field,
  filterModel,
  gridRef,
  onClear
}: FilterExplanationProps): JSX.Element => {
  if (isCombinedFilter(filterModel)) {
    return (
      <span className={chipClassName}>
        {fieldLabel(field)}
        {(filterModel.conditions || []).map((condition, index) => {
          const { filter, type: op } = condition;
          const rowFlagOption = findRowFlagFilterOption(field, op);
          return (
            <React.Fragment key={index}>
              {index > 0 && <span className={operatorClassName}>{filterModel.operator}</span>}
              {rowFlagOption ? (
                <span className={valueClassName}>{rowFlagOption.displayName}</span>
              ) : (
                <>
                  <span className={operatorClassName}>
                    {FILTER_LOCALE_TEXT[op as keyof typeof FILTER_LOCALE_TEXT]}
                  </span>
                  <span className={valueClassName}>{filter}</span>
                </>
              )}
            </React.Fragment>
          );
        })}
        <ClearFilterButton field={field} onClear={onClear} />
      </span>
    );
  }

  const { filterType, type: op } = filterModel;

  const rowFlagOption = findRowFlagFilterOption(field, op);
  if (rowFlagOption) {
    return (
      <span className={chipClassName}>
        {fieldLabel(field)}
        <span className={valueClassName}>{rowFlagOption.displayName}</span>
        <ClearFilterButton field={field} onClear={onClear} />
      </span>
    );
  }

  switch (filterType) {
    case 'text':
    case 'number': {
      const { filter } = filterModel;
      // AG Grid's boolean-column filter (e.g. on_hold) reports its operator as
      // the type ('true'/'false') and carries no `filter` value, so render the
      // boolean directly rather than the operator locale + "undefined".
      const isBooleanOp = (op as string) === 'true' || (op as string) === 'false';
      return (
        <span className={chipClassName}>
          {titleize(field)}
          <span className={operatorClassName}>
            {isBooleanOp ? 'is' : FILTER_LOCALE_TEXT[op as keyof typeof FILTER_LOCALE_TEXT]}
          </span>
          <span className={valueClassName}>{isBooleanOp ? op : String(filter)}</span>
          <ClearFilterButton field={field} onClear={onClear} />
        </span>
      );
    }

    case 'date': {
      // Check if this column's filterParams has includeTime set to false
      let shouldHideTime = false;
      if (gridRef?.current?.api) {
        const column = gridRef.current.api.getColumn(field);
        if (column) {
          const colDef = column.getColDef();
          const filterParams = colDef.filterParams as { includeTime?: boolean } | undefined;
          shouldHideTime = filterParams?.includeTime === false;
        }
      }

      // Format date strings based on includeTime setting
      const formatDate = (dateStr: string | null | undefined): string => {
        if (!dateStr) return '';
        if (shouldHideTime) {
          // Extract just the date part from ISO string (YYYY-MM-DD)
          const match = dateStr.match(/^\d{4}-\d{2}-\d{2}/);
          return match ? match[0] : dateStr;
        }
        return dateStr;
      };

      return (
        <span className={chipClassName}>
          {titleize(field)}
          <span className={operatorClassName}>
            {FILTER_LOCALE_TEXT[op as keyof typeof FILTER_LOCALE_TEXT]}
          </span>
          <span className={valueClassName}>
            {filterModel.dateTo ? (
              <>
                {formatDate(filterModel.dateFrom)} and {formatDate(filterModel.dateTo)}
              </>
            ) : (
              formatDate(filterModel.dateFrom)
            )}
          </span>
          <ClearFilterButton field={field} onClear={onClear} />
        </span>
      );
    }
    default:
      return (
        <span className={chipClassName}>
          Filter by {titleize(field)}
          <ClearFilterButton field={field} onClear={onClear} />
        </span>
      );
  }
};
export default FilterExplanation;
